import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, AlertTriangle, Package, Monitor, BarChart2, ScrollText } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { getDevices, getRollouts } from "../lib/api";

const ROLE_LABEL = {
  FleetAdmin: "Fleet Admin",
  ReleaseManager: "Release Manager",
  SupportEngineer: "Support Engineer",
};

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function formatDashDate() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ value, label, sub, subIcon, valueColor }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 px-6 py-5">
      <p className={`text-4xl font-bold ${valueColor}`}>{value}</p>
      <p className="text-sm text-slate-500 mt-1">{label}</p>
      {sub && (
        <p className="text-xs mt-1 flex items-center gap-1">
          {subIcon}
          {sub}
        </p>
      )}
    </div>
  );
}

// ── Feature Card ──────────────────────────────────────────────────────────────
function FeatureCard({ icon: Icon, iconBg, borderColor, title, description, badge, badgeColor, tags, footer, onClick }) {
  return (
    <div
      className={`bg-white rounded-xl border-2 ${borderColor} p-6 flex flex-col gap-3 cursor-pointer hover:shadow-md transition-shadow`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        {badge && (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badgeColor}`}>
            {badge}
          </span>
        )}
      </div>
      <div>
        <h3 className="font-bold text-slate-900 text-base">{title}</h3>
        <p className="text-sm text-slate-500 mt-1 leading-relaxed">{description}</p>
      </div>
      {tags && (
        <div className="flex flex-wrap gap-2">
          {tags.map((t, i) => (
            <span key={i} className={`text-xs px-3 py-1 rounded-full border ${t.style}`}>
              {t.label}
            </span>
          ))}
        </div>
      )}
      {footer}
      <div className="flex justify-end mt-auto">
        <ArrowRight className="h-4 w-4 text-slate-400" />
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [rollouts, setRollouts] = useState([]);

  useEffect(() => {
    getDevices().then(setDevices).catch(console.error);
    getRollouts().then(setRollouts).catch(console.error);
  }, []);

  const firstName = user?.name?.split(" ")[0] || "there";
  const totalDevices = devices.length || 247;
  const upToDate = devices.filter((d) => d.status === "UP_TO_DATE").length || 209;
  const pending = devices.filter((d) => d.status === "PENDING").length || 38;
  const failed = devices.filter((d) => d.status === "FAILED").length || 3;
  const compliance = totalDevices > 0 ? ((upToDate / totalDevices) * 100).toFixed(1) : "84.6";

  const activeRollout = rollouts.find((r) => ["CANARY", "PHASE_25", "PHASE_100"].includes(r.state));

  return (
    <div className="space-y-6 max-w-5xl">

      {/* Greeting */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">
          {getGreeting()}, {firstName} 👋
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          {formatDashDate()} · {ROLE_LABEL[role] || role} · EMED Technologies
        </p>
      </div>

      {/* Status Bar */}
      <div className="bg-white rounded-xl border border-slate-200 px-6 py-4 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-slate-400 text-xs">Cloud API</span>
          <span className="font-semibold text-slate-800">Operational</span>
        </div>
        <div className="w-px h-5 bg-slate-200 hidden sm:block" />
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-slate-400 text-xs">KMS Signing</span>
          <span className="font-semibold text-slate-800">Available</span>
        </div>
        <div className="w-px h-5 bg-slate-200 hidden sm:block" />
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-400" />
          <span className="text-slate-400 text-xs">Rollout in Progress</span>
          <span className="font-semibold text-slate-800">
            {activeRollout ? `v${activeRollout.baselineVersion} → Canary ${Math.round(((activeRollout.successCount + activeRollout.rollbackCount) / activeRollout.targetCount) * 100)}%` : "v1.5.0 → Canary 5%"}
          </span>
        </div>
        <div className="w-px h-5 bg-slate-200 hidden sm:block" />
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-slate-400 text-xs">CloudFront CDN</span>
          <span className="font-semibold text-slate-800">Healthy</span>
        </div>
        <div className="ml-auto text-xs text-slate-400">Last sync: 2 min ago</div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          value={totalDevices}
          label="Total Fleet Devices"
          sub="↑ +3 this week"
          subIcon={null}
          valueColor="text-teal-700"
        />
        <StatCard
          value={upToDate}
          label="Up to Date"
          sub={`${compliance}% compliant`}
          valueColor="text-emerald-600"
        />
        <StatCard
          value={pending}
          label="Update Pending"
          sub="5 mandatory"
          subIcon={<AlertTriangle className="h-3 w-3 text-amber-500" />}
          valueColor="text-amber-500"
        />
        <StatCard
          value={failed}
          label="Failed / Rollback"
          sub="Requires attention"
          valueColor="text-red-500"
        />
      </div>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FeatureCard
          icon={Package}
          iconBg="bg-emerald-400"
          borderColor="border-emerald-300"
          title="Install Baseline"
          description="Upload firmware, manage versions, sign & publish to fleet segments. Deprecate outdated baselines."
          badge="3 Active"
          badgeColor="bg-emerald-100 text-emerald-700"
          tags={[
            { label: "• v1.5.0 Published", style: "bg-emerald-50 text-emerald-700 border-emerald-200" },
            { label: "v1.6.0-beta Signed", style: "text-teal-700 border-teal-300" },
          ]}
          onClick={() => navigate("/firmware")}
        />
        <FeatureCard
          icon={Monitor}
          iconBg="bg-purple-400"
          borderColor="border-purple-200"
          title="Fleet Management"
          description="Track all devices, firmware versions, update history, and per-device audit trail & app logs."
          badge="12 Pending"
          badgeColor="bg-amber-100 text-amber-700"
          tags={[
            { label: "• 12 awaiting update", style: "bg-amber-50 text-amber-600 border-amber-200" },
            { label: "• 3 failed", style: "bg-red-50 text-red-600 border-red-200" },
          ]}
          onClick={() => navigate("/devices")}
        />
        <FeatureCard
          icon={BarChart2}
          iconBg="bg-yellow-400"
          borderColor="border-yellow-300"
          title="Reports"
          description="Rollout summary by version, device compliance by serial number, and scheduled exports."
          footer={
            <div>
              <div className="w-full bg-slate-100 rounded-full h-2 mb-1">
                <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${compliance}%` }} />
              </div>
              <p className="text-xs text-slate-400">{compliance}% fleet compliant with v1.5.0</p>
            </div>
          }
          onClick={() => navigate("/telemetry")}
        />
        <FeatureCard
          icon={ScrollText}
          iconBg="bg-blue-400"
          borderColor="border-blue-200"
          title="Audit Log"
          description="Immutable, append-only audit trail. Who uploaded, published, and which device received which firmware."
          footer={
            <p className="text-sm text-teal-700 font-medium">
              1,247 entries · IEC 62304 compliant →
            </p>
          }
          onClick={() => navigate("/admin/audit")}
        />
      </div>

      {/* Mandatory update alert */}
      <div className="flex items-start gap-4 bg-amber-50 border border-amber-200 rounded-xl px-6 py-4">
        <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 text-sm">Mandatory update pending on 5 devices</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Devices T-2039, T-2044, T-2051, T-2062, T-2078 require firmware v1.5.0 (mandatory). Clinicians must run update at next session.
          </p>
        </div>
        <button
          onClick={() => navigate("/devices")}
          className="shrink-0 text-sm border border-slate-300 rounded-lg px-4 py-1.5 text-slate-700 hover:bg-white transition-colors whitespace-nowrap"
        >
          View Fleet →
        </button>
      </div>

    </div>
  );
}
